import { Button } from '@mui/material';
import { IconWrapper, pxToRem } from '@geneo2-web/shared-ui';
import {
  StudentOmrPerformanceInfo,
  StudentPerformanceInfo,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';

type StudentResponseRow = StudentPerformanceInfo | StudentOmrPerformanceInfo;

const yesNo = (value?: boolean) => (value ? 'Yes' : 'No');

const escapeXml = (value: string | number) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const crc32 = (data: Uint8Array) => {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i];
    for (let bit = 0; bit < 8; bit++) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const concatBytes = (parts: Uint8Array[]) => {
  const total = parts.reduce((size, part) => size + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};

/** Uncompressed zip. Excel opens this as a normal .xlsx. */
const createZip = (files: { name: string; data: Uint8Array }[]) => {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  files.forEach((file) => {
    const name = encoder.encode(file.name);
    const crc = crc32(file.data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(8, 0, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, file.data.length, true);
    local.setUint32(22, file.data.length, true);
    local.setUint16(26, name.length, true);
    locals.push(new Uint8Array(local.buffer), name, file.data);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true);
    central.setUint16(6, 20, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, file.data.length, true);
    central.setUint32(24, file.data.length, true);
    central.setUint16(28, name.length, true);
    central.setUint32(42, offset, true);
    centrals.push(new Uint8Array(central.buffer), name);
    offset += 30 + name.length + file.data.length;
  });

  const centralDirectory = concatBytes(centrals);
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(8, files.length, true);
  eocd.setUint16(10, files.length, true);
  eocd.setUint32(12, centralDirectory.length, true);
  eocd.setUint32(16, offset, true);

  return new Blob(
    [concatBytes(locals), centralDirectory, new Uint8Array(eocd.buffer)],
    {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }
  );
};

const columnLetter = (index: number) => String.fromCharCode(65 + index);

const xmlCell = (
  column: number,
  row: number,
  value: string | number,
  styleIndex: number
) => {
  const ref = `${columnLetter(column)}${row}`;
  const style = ` s="${styleIndex}"`;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"${style}><v>${value}</v></c>`;
  }
  return `<c r="${ref}"${style} t="inlineStr"><is><t xml:space="preserve">${escapeXml(
    value
  )}</t></is></c>`;
};

const scorePercentLabel = (student: StudentResponseRow) =>
  student.scorePercent
    ? `${student.scorePercent}%`
    : student.isSubmitted && student.isEvaluated
    ? '0%'
    : '-';

const fileToken = (value?: string) =>
  value
    ?.replace(/[\\/:*?"<>|]+/g, ' ')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');

export const studentResponseStatusFileName = ({
  assessmentName,
  subject,
  grade,
  section,
}: {
  assessmentName?: string;
  subject?: string;
  grade?: string;
  section?: string;
}) => {
  const name = [assessmentName, subject, grade, section]
    .map(fileToken)
    .filter(Boolean)
    .join('_');
  return `${name ? `${name}_` : ''}student_response_status.xlsx`;
};

export const downloadStudentResponseStatus = (
  students: StudentResponseRow[],
  maxMarks?: number,
  fileName?: string
) => {
  const header = [
    'Student Name',
    'Has Submitted',
    'Is Evaluated',
    'Score',
    'Max Marks',
    'Score Percent',
  ];
  const rows: (string | number)[][] = students.map((student) => [
    student.name,
    yesNo(student.isSubmitted),
    yesNo(student.isEvaluated),
    student.responseScore ?? '',
    maxMarks ?? '',
    scorePercentLabel(student),
  ]);
  const rightAlignedColumns = new Set([3, 4, 5]);
  const sheetRows = [header, ...rows]
    .map((row, rowIndex) => {
      const cells = row
        .map((cell, columnIndex) => {
          const isHeader = rowIndex === 0;
          const rightAlign = rightAlignedColumns.has(columnIndex);
          const styleIndex = isHeader ? (rightAlign ? 4 : 3) : rightAlign ? 2 : 1;
          return xmlCell(columnIndex, rowIndex + 1, cell, styleIndex);
        })
        .join('');
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join('');
  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <cols>
    <col min="1" max="1" width="28" customWidth="1"/>
    <col min="2" max="3" width="16" customWidth="1"/>
    <col min="4" max="6" width="16" customWidth="1"/>
  </cols>
  <sheetData>${sheetRows}</sheetData>
</worksheet>`;
  const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="3">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF3F4F6"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFD0D5DD"/></left>
      <right style="thin"><color rgb="FFD0D5DD"/></right>
      <top style="thin"><color rgb="FFD0D5DD"/></top>
      <bottom style="thin"><color rgb="FFD0D5DD"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="5">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"/>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="right"/></xf>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left"/></xf>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;
  const encoder = new TextEncoder();
  const blob = createZip([
    {
      name: '[Content_Types].xml',
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`),
    },
    {
      name: '_rels/.rels',
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    },
    {
      name: 'xl/workbook.xml',
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="Submission Status" sheetId="1" r:id="rId1"/></sheets>
</workbook>`),
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    },
    { name: 'xl/styles.xml', data: encoder.encode(styles) },
    { name: 'xl/worksheets/sheet1.xml', data: encoder.encode(sheet) },
  ]);
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName || 'student_response_status.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

export const DownloadStudentResponseStatusButton = ({
  students,
  maxMarks,
  assessmentName,
  subject,
  grade,
  section,
}: {
  students?: StudentResponseRow[];
  maxMarks?: number;
  assessmentName?: string;
  subject?: string;
  grade?: string;
  section?: string;
}) => {
  if (!students?.length) return null;

  return (
    <Button
      onClick={() =>
        downloadStudentResponseStatus(
          students,
          maxMarks,
          studentResponseStatusFileName({
            assessmentName,
            subject,
            grade,
            section,
          })
        )
      }
      sx={{
        cursor: 'pointer',
        border: '1px solid #158D29',
        borderRadius: pxToRem(10),
        color: '#0AA34F',
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(10),
        textTransform: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <IconWrapper
        name="download"
        size="md"
        parentFolder="icons"
        type="png"
        customSx={{
          width: pxToRem(20),
          height: pxToRem(20),
        }}
      />
      Submission Status
    </Button>
  );
};
